import React, { Suspense, lazy, useState, useRef, useEffect, useMemo } from 'react';
import { Download, Upload, Plus, Minus, Printer, RotateCcw, ClipboardPaste, X, AlertCircle, Loader2 } from 'lucide-react';
import { HIDDEN_FILE_INPUT_CLASS, JSON_ACCEPT, openFileInput } from '../../../utils/filePicker';
import {
  clearPlannerSnapshot,
  purgeStoredPlannerData,
  setPlannerSnapshot
} from '../utils/plannerSnapshot';
import PlannerTextField, { clearRememberedSizes } from './PlannerTextField';
import ReferenceFileManager from './ReferenceFileManager';
import ActivityDragHandle, { useActivityReorder } from './ActivityDragHandle';
import { insertActivityAfter, moveActivity, removeActivityAt } from '../utils/activityList';
import { buildExportDocument, buildPrintDocument } from '../utils/plannerExport';
import {
  MAX_TOTAL_REFERENCE_CHARS,
  normalizeReferenceFiles,
  totalReferenceChars
} from '../utils/referenceFiles';
import {
  RICH_TEXT_ACTIVITY_FIELDS,
  RICH_TEXT_FIELDS,
  getRichText,
  normalizeRichTextMap,
  setRichText
} from '../utils/plannerText';
import { formatTotalActivityTime } from '../utils/duration';
import { PLANNER_SAMPLE_LESSON } from '../utils/sampleLesson';

/**
 * The formatting editor, loaded the first time someone opens one.
 *
 * TipTap and ProseMirror are the largest thing in the bundle and nothing needs
 * them to fill in a lesson plan — the fields on this page are textareas. See
 * components/LazyEditors.js for the same split on the deck editors, and
 * components/__tests__/lazyEditors.test.js for the guard that keeps it.
 */
const PlannerRichTextModal = lazy(() => import('./PlannerRichTextModal'));

const emptyLessonData = {
  lessonTopic: '',
  bridgeIn: '',
  objective: '',
  preAssessment: '',
  activities: [
    { title: '', duration: '', facilitatorActivity: '', learnerActivity: '', materialsRequired: '', learnerMaterials: '' }
  ],
  postAssessment: '',
  summary: '',
  reflections: ''
};

const formatWeekLabel = (weekValue) => `week_${weekValue}`;
const formatVersionLabel = (versionValue) => String(versionValue).padStart(3, '0');

const normalizeWeekValue = (value = '') => {
  if (!value) return '';
  const cleaned = String(value).toLowerCase().replace(/[^0-9]/g, '');
  const numeric = Number.parseInt(cleaned, 10);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  return String(numeric).padStart(3, '0');
};

const normalizeVersionValue = (value = '') => {
  if (!value) return '';
  const cleaned = String(value).replace(/[^0-9]/g, '');
  const numeric = Number.parseInt(cleaned, 10);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  return formatVersionLabel(numeric);
};

/**
 * The header fields — document name, week and version — read out of a saved
 * plan.
 *
 * Absent keys come back empty rather than being left alone, because the header
 * describes the plan on screen: loading a file that carries no name over one
 * that did would otherwise leave the previous plan's name on the new one, and
 * the next Save JSON would write it into the filename.
 */
const readPlanMetadata = (data = {}) => ({
  documentName: typeof data.documentName === 'string' ? data.documentName : '',
  selectedWeek: normalizeWeekValue(data.selectedWeek),
  exportVersionLabel: normalizeVersionValue(data.exportVersion)
});

const BopppsPlanner = () => {
  /**
   * Bring a loaded plan up to the shape this page renders, without changing
   * what a plan from an older version means.
   *
   * Files written before the formatting editor and the reference attachments
   * existed simply have neither `richText` nor `referenceFiles`, and read here
   * as "plain text, nothing attached" — which is exactly what they are. Both
   * additions are optional and are dropped from the saved JSON again when they
   * are empty, so a plan that uses no formatting still round-trips as the same
   * file it always was.
   */
  const coerceLessonData = (data) => {
    const source = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
    const activities = Array.isArray(source.activities) && source.activities.length
      ? source.activities
      : emptyLessonData.activities;

    const coerced = {
      ...emptyLessonData,
      ...source,
      activities: activities.map((activity) => {
        const entry = activity && typeof activity === 'object' && !Array.isArray(activity) ? activity : {};
        const next = {
          // A short name for the row, so a long plan can be scanned. Optional
          // — an activity with no name simply prints as "Activity 3".
          title: typeof entry.title === 'string' ? entry.title : '',
          duration: typeof entry.duration === 'string' || typeof entry.duration === 'number' ? String(entry.duration) : '',
          facilitatorActivity: typeof entry.facilitatorActivity === 'string' ? entry.facilitatorActivity : '',
          // Added when the activity cell was split in two. A plan from before
          // that has everything in facilitatorActivity, which is where it
          // stays — moving someone's words between boxes on their behalf
          // would be guessing which half they meant.
          learnerActivity: typeof entry.learnerActivity === 'string' ? entry.learnerActivity : '',
          materialsRequired: typeof entry.materialsRequired === 'string' ? entry.materialsRequired : '',
          // Split from materialsRequired the same way and for the same reason:
          // what you set up is not what the students need at their desk.
          learnerMaterials: typeof entry.learnerMaterials === 'string' ? entry.learnerMaterials : ''
        };
        const richText = normalizeRichTextMap(entry.richText, RICH_TEXT_ACTIVITY_FIELDS);
        if (richText) next.richText = richText;
        const referenceFiles = normalizeReferenceFiles(entry.referenceFiles);
        if (referenceFiles.length) next.referenceFiles = referenceFiles;
        return next;
      })
    };

    const richText = normalizeRichTextMap(source.richText, RICH_TEXT_FIELDS);
    if (richText) coerced.richText = richText;
    else delete coerced.richText;

    // Never carried in the lesson body — these are the header controls, and
    // leaving them in would write them twice into the saved JSON.
    delete coerced.documentName;
    delete coerced.selectedWeek;
    delete coerced.exportVersion;

    return coerced;
  };

  // The planner always opens empty. Nothing is read back from the browser:
  // a lesson left on a shared machine is the thing this page must not do, and
  // Upload JSON is how a plan comes back. See utils/plannerSnapshot.js.
  const [lessonData, setLessonData] = useState(emptyLessonData);
  const [documentName, setDocumentName] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [exportVersion, setExportVersion] = useState(1);
  const [exportVersionInput, setExportVersionInput] = useState('');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [pasteJsonText, setPasteJsonText] = useState('');
  const [pasteJsonError, setPasteJsonError] = useState('');
  // Print and Export HTML leave the instructor's half out when this is on —
  // see utils/plannerExport.js for exactly what a handout omits.
  const [studentHandout, setStudentHandout] = useState(false);
  // Which field the formatting editor is open on: null, or
  // { field, label, activityIndex? }.
  const [editorTarget, setEditorTarget] = useState(null);

  const fileInputRef = useRef(null);
  const exportVersionRef = useRef(1);

  /**
   * The whole plan as it is written to a file — the lesson body plus the
   * header fields.
   *
   * One shape for both Save JSON and the in-memory hand-off, so the AI Prompt
   * page sees exactly what a saved file would carry. Anything reading it for
   * the lesson alone (that page's "Load from Planner", the companion content
   * pack) reads the same lesson fields it always did and ignores the three
   * header keys.
   */
  const buildPlanPayload = (versionLabel) => ({
    ...lessonData,
    documentName,
    selectedWeek: selectedWeek ? formatWeekLabel(selectedWeek) : '',
    exportVersion: versionLabel
  });

  // Clear out anything a version that did autosave left behind. The copies
  // sitting on shared machines are the problem, and opening the app on one is
  // the only chance this code gets to remove them.
  useEffect(() => {
    purgeStoredPlannerData();
  }, []);

  // Hand the current plan to the pages that read it — the AI Prompt page's
  // "Load from Planner" and the companion-pack export. In memory only: both
  // are tabs in this same page, and nothing here reaches browser storage.
  useEffect(() => {
    setPlannerSnapshot(buildPlanPayload(resolveCurrentExportVersionLabel()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonData, documentName, selectedWeek, exportVersionInput, exportVersion]);

  const handleInputChange = (field, value) => {
    setLessonData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleActivityChange = (index, field, value) => {
    setLessonData(prev => ({
      ...prev,
      activities: prev.activities.map((activity, i) =>
        i === index ? { ...activity, [field]: value } : activity
      )
    }));
  };

  const handleActivityReferenceChange = (index, files) => {
    setLessonData(prev => ({
      ...prev,
      activities: prev.activities.map((activity, i) => {
        if (i !== index) return activity;
        const next = { ...activity };
        if (files.length) next.referenceFiles = files;
        else delete next.referenceFiles;
        return next;
      })
    }));
  };

  /** Drop a field's formatting, keeping the plain text it flattens to. */
  const handleClearFormatting = (field, activityIndex) => {
    setLessonData(prev => {
      if (activityIndex === undefined) return setRichText(prev, field, '');
      return {
        ...prev,
        activities: prev.activities.map((activity, i) =>
          i === activityIndex ? setRichText(activity, field, '') : activity
        )
      };
    });
  };

  /**
   * Store what the formatting editor produced: the HTML for this page, the
   * print sheet and the export, and the plain-text flattening for the AI
   * Prompt page, the companion pack and every older reader of the JSON.
   */
  const handleEditorSave = (html, plain) => {
    const target = editorTarget;
    if (!target) return;
    setLessonData(prev => {
      if (target.activityIndex === undefined) {
        return { ...setRichText(prev, target.field, html), [target.field]: plain };
      }
      return {
        ...prev,
        activities: prev.activities.map((activity, i) =>
          i === target.activityIndex
            ? { ...setRichText(activity, target.field, html), [target.field]: plain }
            : activity
        )
      };
    });
    setEditorTarget(null);
  };

  // The "+" belongs to the row it sits in, so the new activity goes directly
  // below that row rather than at the end of the lesson.
  const addActivity = (index) => {
    setLessonData(prev => ({
      ...prev,
      activities: insertActivityAfter(prev.activities, index, { title: '', duration: '', facilitatorActivity: '', learnerActivity: '', materialsRequired: '', learnerMaterials: '' })
    }));
  };

  const removeActivity = (index) => {
    setLessonData(prev => ({
      ...prev,
      activities: removeActivityAt(prev.activities, index)
    }));
  };

  const reorderActivity = (from, to) => {
    setLessonData(prev => ({
      ...prev,
      activities: moveActivity(prev.activities, from, to)
    }));
  };

  const resolveExportVersionNumber = () => {
    const manualVersion = Number.parseInt(exportVersionInput.trim(), 10);
    if (Number.isFinite(manualVersion) && manualVersion > 0) {
      exportVersionRef.current = manualVersion;
      setExportVersion(manualVersion);
      return manualVersion;
    }
    return exportVersionRef.current;
  };

  const resolveCurrentExportVersionLabel = () => {
    const manualVersion = Number.parseInt(exportVersionInput.trim(), 10);
    if (Number.isFinite(manualVersion) && manualVersion > 0) {
      return formatVersionLabel(manualVersion);
    }
    return formatVersionLabel(exportVersionRef.current);
  };

  const resolveExportBaseName = (versionLabel = resolveCurrentExportVersionLabel()) => {
    const rawName = documentName.trim() || 'boppps_lesson_plan';
    const sanitizedName = rawName.replace(/[\\/?:%*|"<>]/g, '_').trim() || 'boppps_lesson_plan';
    const weekSuffix = selectedWeek ? `_${formatWeekLabel(selectedWeek)}` : '';
    return `${sanitizedName}${weekSuffix}_v${versionLabel}`;
  };

  const resolveExportTitle = () => {
    return documentName.trim() || lessonData.lessonTopic || 'BOPPPS Lesson Planning Template';
  };

  /**
   * Apply a plan parsed from a file or a paste, header fields included.
   *
   * The name, week and version are applied as a set, cleared included: the
   * header has to describe the plan that is now on screen. Carrying the last
   * plan's name over onto a file that has none is how a lesson gets saved back
   * out under the wrong name.
   */
  const applyLoadedPlan = (jsonData) => {
    const meta = readPlanMetadata(jsonData);
    setLessonData(coerceLessonData(jsonData));
    setDocumentName(meta.documentName);
    setSelectedWeek(meta.selectedWeek);
    setExportVersionInput(meta.exportVersionLabel);
    const loadedVersion = Number.parseInt(meta.exportVersionLabel, 10) || 1;
    exportVersionRef.current = loadedVersion;
    setExportVersion(loadedVersion);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    // Some systems hand back an empty type for .json, so the extension is
    // checked too rather than refusing a file the picker itself offered.
    const looksLikeJson = file && (file.type === 'application/json' || /\.json$/i.test(file.name || ''));
    if (looksLikeJson) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          applyLoadedPlan(JSON.parse(e.target.result));
        } catch (error) {
          alert('Error parsing JSON file. Please check the file format.');
        }
      };
      reader.readAsText(file);
    } else {
      alert('Please select a valid JSON file.');
    }
    // Reset file input
    event.target.value = '';
  };

  const handleSave = (versionLabelOverride) => {
    const exportVersionNumber = Number.isFinite(versionLabelOverride)
      ? versionLabelOverride
      : resolveExportVersionNumber();
    const exportVersionLabel = formatVersionLabel(exportVersionNumber);
    // The same payload the AI Prompt page is handed, so what you save is what
    // the next step of the workflow sees.
    const dataStr = JSON.stringify(buildPlanPayload(exportVersionLabel), null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `${resolveExportBaseName(exportVersionLabel)}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const handleVersionUp = () => {
    const baseVersion = resolveExportVersionNumber();
    const nextVersion = baseVersion + 1;
    exportVersionRef.current = nextVersion;
    setExportVersion(nextVersion);
    setExportVersionInput(formatVersionLabel(nextVersion));
    handleSave(nextVersion);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('The print window was blocked. Allow pop-ups for this site and try again.');
      return;
    }
    printWindow.document.write(buildPrintDocument(lessonData, {
      title: lessonData.lessonTopic,
      studentHandout
    }));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleHTMLExport = () => {
    const dataUri = 'data:text/html;charset=utf-8,'
      + encodeURIComponent(buildExportDocument(lessonData, {
        title: resolveExportTitle(),
        studentHandout
      }));
    const exportFileDefaultName = `${resolveExportBaseName()}.html`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const loadSample = () => {
    setLessonData(coerceLessonData(PLANNER_SAMPLE_LESSON));
  };

  const handleReset = () => {
    if (!window.confirm('Reset the form? All current data will be cleared.')) return;
    setLessonData(emptyLessonData);
    setDocumentName('');
    setSelectedWeek('');
    exportVersionRef.current = 1;
    setExportVersion(1);
    setExportVersionInput('');
    clearPlannerSnapshot();
    purgeStoredPlannerData();
    clearRememberedSizes();
  };

  const handleOpenPasteModal = () => {
    setPasteJsonText('');
    setPasteJsonError('');
    setShowPasteModal(true);
  };

  const handleApplyPastedJson = () => {
    try {
      applyLoadedPlan(JSON.parse(pasteJsonText));
      setShowPasteModal(false);
      setPasteJsonText('');
      setPasteJsonError('');
    } catch (err) {
      setPasteJsonError('Invalid JSON — please check your input and try again.');
    }
  };

  const referenceBudget = useMemo(
    () => Math.max(0, MAX_TOTAL_REFERENCE_CHARS - totalReferenceChars(lessonData.activities)),
    [lessonData.activities]
  );

  const totalActivityTime = useMemo(
    () => formatTotalActivityTime(lessonData.activities),
    [lessonData.activities]
  );

  const activityReorder = useActivityReorder(reorderActivity, lessonData.activities.length);

  /** Props shared by every lesson-level field. */
  const lessonField = (field, label) => ({
    label,
    name: label,
    fieldKey: field,
    value: lessonData[field],
    richHtml: getRichText(lessonData, field),
    onChange: (value) => handleInputChange(field, value),
    onOpenEditor: () => setEditorTarget({ field, label }),
    onClearFormatting: () => handleClearFormatting(field)
  });

  /** Props shared by every activity-level field. */
  const activityField = (activity, index, field, label) => ({
    label: null,
    name: `${label} (activity ${index + 1})`,
    fieldKey: `activity.${index}.${field}`,
    value: activity[field],
    richHtml: getRichText(activity, field),
    onChange: (value) => handleActivityChange(index, field, value),
    onOpenEditor: () => setEditorTarget({ field, label: `${label} — Activity ${index + 1}`, activityIndex: index }),
    onClearFormatting: () => handleClearFormatting(field, index)
  });

  const editorValue = editorTarget
    ? (editorTarget.activityIndex === undefined
      ? lessonData
      : lessonData.activities[editorTarget.activityIndex])
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-6xl mx-auto p-6 bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-center mb-2 text-gray-800">
            BOPPPS Lesson Planning Template
          </h1>

          <div className="flex flex-wrap items-end justify-center gap-4 mb-4">
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
              Name
              <input
                type="text"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter document name"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
              Week #
              <select
                value={selectedWeek}
                onChange={(e) => setSelectedWeek(e.target.value)}
                className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select week</option>
                {Array.from({ length: 15 }, (_, index) => {
                  const weekValue = String(index + 1).padStart(3, '0');
                  return (
                    <option key={weekValue} value={weekValue}>
                      {formatWeekLabel(weekValue)}
                    </option>
                  );
                })}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
              Version #
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={exportVersionInput}
                  onChange={(e) => setExportVersionInput(e.target.value)}
                  className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm font-normal text-gray-800 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={`Auto v${formatVersionLabel(exportVersion)}`}
                />
                <button
                  type="button"
                  onClick={handleVersionUp}
                  className="rounded-md border border-blue-500 px-3 py-2 text-sm font-semibold text-blue-600 transition-colors hover:bg-blue-50"
                >
                  Version Up
                </button>
              </div>
            </label>
          </div>

          {/* File Operations */}
          <div className="flex justify-center gap-3 mb-6 no-print flex-wrap">
            <button
              onClick={() => openFileInput(fileInputRef.current)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              <Upload size={16} />
              Upload JSON
            </button>
            <button
              onClick={handleOpenPasteModal}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
            >
              <ClipboardPaste size={16} />
              Paste JSON
            </button>
            <button
              onClick={loadSample}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
            >
              Load Sample
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <Download size={16} />
              Save JSON
            </button>
            <button
              onClick={handleHTMLExport}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Download size={16} />
              Export HTML
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Printer size={16} />
              Print
            </button>
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={JSON_ACCEPT}
              onChange={handleFileUpload}
              className={HIDDEN_FILE_INPUT_CLASS}
            />
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-center gap-2 no-print">
            <label className="flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900">
              <input
                type="checkbox"
                checked={studentHandout}
                onChange={(e) => setStudentHandout(e.target.checked)}
              />
              <span>
                <strong>Student handout</strong> — Print and Export HTML leave out your
                instructor notes, instructor materials, reference material and reflections
              </span>
            </label>
          </div>

          <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 no-print">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>
              This plan is not saved anywhere — closing or reloading the tab clears it, and
              nothing is left on this computer. Use <strong>Save JSON</strong> to keep it, and
              <strong> Upload JSON</strong> to bring it back.
            </span>
          </div>
        </div>

        {/* Main Table */}
        <div className="border border-gray-300 overflow-hidden shadow-sm">
          {/* Lesson Topic */}
          <div className="border-b border-gray-300 min-h-16 flex">
            <div className="bg-gray-100 p-3 font-bold border-r border-gray-300" style={{width: '160px', minWidth: '160px'}}>
              <div className="flex items-start h-full pt-2">Lesson Topic</div>
            </div>
            <div className="flex-1 p-3">
              <input
                type="text"
                value={lessonData.lessonTopic}
                onChange={(e) => handleInputChange('lessonTopic', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter lesson topic"
              />
            </div>
          </div>

          {/* Bridge-In */}
          <div className="border-b border-gray-300 flex">
            <div className="bg-gray-100 p-3 font-bold border-r border-gray-300" style={{width: '160px', minWidth: '160px'}}>
              <div className="flex items-start h-full pt-1">Bridge-In</div>
            </div>
            <div className="flex-1 p-3">
              <PlannerTextField
                {...lessonField('bridgeIn', 'Bridge-In')}
                label={null}
                minHeight={110}
                placeholder="Describe how you will connect with learners and introduce the topic"
              />
            </div>
          </div>

          {/* Objective(s) and Pre-Assessment */}
          <div className="border-b border-gray-300 flex">
            <div className="bg-gray-100 p-3 font-bold border-r border-gray-300" style={{width: '160px', minWidth: '160px'}}>
              <div className="flex items-start h-full pt-1">Objective(s)</div>
            </div>
            <div className="p-3 border-r border-gray-300" style={{width: 'calc(100% - 460px)'}}>
              <PlannerTextField
                {...lessonField('objective', 'Objective(s)')}
                label={null}
                minHeight={180}
                placeholder="List learning objectives (use bullet points or numbers)"
              />
            </div>
            <div className="p-3" style={{width: '300px', minWidth: '300px'}}>
              <PlannerTextField
                {...lessonField('preAssessment', 'Pre-Assessment')}
                minHeight={180}
                placeholder="How will you assess prior knowledge?"
              />
            </div>
          </div>

          {/* Presentation/Practice/Participation Header */}
          <div className="border-b border-gray-300">
            <div className="bg-black text-white p-3 font-bold text-center text-sm">
              Presentation/Practice/Participation
            </div>
            <div className="min-h-12 flex bg-gray-50">
              <div className="p-3 border-r border-gray-300 font-bold text-sm flex items-center" style={{width: '90px', minWidth: '90px'}}>
                Duration (in min.)
              </div>
              <div className="p-3 border-r border-gray-300 font-bold text-left text-sm flex items-center" style={{width: 'calc(100% - 410px)'}}>
                Facilitator or Learner Activity (add rows as needed)
              </div>
              <div className="p-3 font-bold text-center text-sm flex items-center justify-center" style={{width: '320px', minWidth: '320px'}}>
                Materials Required
              </div>
            </div>
          </div>

          {/* Activities */}
          {lessonData.activities.map((activity, index) => (
            <div
              key={index}
              className={`border-b border-gray-300 flex${activityReorder.rowClassName(index)}`}
              {...activityReorder.rowProps(index)}
            >
              <div className="p-3 border-r border-gray-300 flex flex-col items-center gap-2" style={{width: '90px', minWidth: '90px'}}>
                <input
                  type="text"
                  value={activity.duration}
                  onChange={(e) => handleActivityChange(index, 'duration', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="min"
                  aria-label={`Duration for activity ${index + 1}`}
                />
                <div className="flex items-center gap-1">
                  <ActivityDragHandle
                    index={index}
                    count={lessonData.activities.length}
                    {...activityReorder.handleProps(index)}
                  />
                  <button
                    type="button"
                    onClick={() => addActivity(index)}
                    className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-100 transition-colors"
                    title="Add an activity below this one"
                    aria-label={`Add an activity below activity ${index + 1}`}
                  >
                    <Plus size={16} />
                  </button>
                  {lessonData.activities.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeActivity(index)}
                      className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-100 transition-colors"
                      title="Remove Activity"
                      aria-label={`Remove activity ${index + 1}`}
                    >
                      <Minus size={16} />
                    </button>
                  )}
                </div>
              </div>
              <div className="p-3 border-r border-gray-300" style={{width: 'calc(100% - 410px)'}}>
                {/* Two boxes, one object: the instructor's half sits above the
                    students' half inside a single frame, so the pair reads as
                    one activity rather than two unrelated fields. */}
                <div className="overflow-hidden rounded border border-gray-300">
                  <input
                    type="text"
                    value={activity.title || ''}
                    onChange={(e) => handleActivityChange(index, 'title', e.target.value)}
                    className="w-full border-b border-gray-300 bg-gray-100 px-2 py-1.5 text-sm font-semibold text-gray-800 focus:border-blue-500 focus:outline-none"
                    placeholder={`Activity ${index + 1} — name it to make a long plan scannable (optional)`}
                    aria-label={`Name for activity ${index + 1}`}
                  />
                  <div className="border-l-4 border-l-amber-400 bg-gray-50 p-2">
                    <PlannerTextField
                      {...activityField(activity, index, 'facilitatorActivity', 'Instructor notes / reference')}
                      label="Instructor notes / reference"
                      minHeight={110}
                      placeholder="Notes for you: what to say, set up, watch for, or have ready"
                    />
                  </div>
                  {/* Sides set individually: a blanket `border-gray-300`
                      competes with the accent colour and the bar disappears. */}
                  <div className="border-t border-t-gray-300 border-l-4 border-l-sky-400 p-2">
                    <PlannerTextField
                      {...activityField(activity, index, 'learnerActivity', 'Student activity')}
                      label="Student activity"
                      minHeight={110}
                      placeholder="What the students do during this time"
                    />
                  </div>
                </div>
                <ReferenceFileManager
                  files={activity.referenceFiles || []}
                  activityLabel={`activity ${index + 1}`}
                  remainingCharBudget={referenceBudget}
                  onChange={(files) => handleActivityReferenceChange(index, files)}
                />
              </div>
              <div className="p-3" style={{width: '320px', minWidth: '320px'}}>
                {/* Split on the same line as the activity beside it: what you
                    set up, then what the students need in front of them. */}
                <div className="overflow-hidden rounded border border-gray-300">
                  <div className="border-l-4 border-l-amber-400 bg-gray-50 p-2">
                    <PlannerTextField
                      {...activityField(activity, index, 'materialsRequired', 'Instructor materials')}
                      label="Instructor materials"
                      minHeight={90}
                      placeholder="What you set up or bring"
                    />
                  </div>
                  <div className="border-t border-t-gray-300 border-l-4 border-l-sky-400 p-2">
                    <PlannerTextField
                      {...activityField(activity, index, 'learnerMaterials', 'Student materials')}
                      label="Student materials"
                      minHeight={90}
                      placeholder="What students need at their desk"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Total activity time. Derived from the duration column on every
              render, so it follows whatever is typed there without anyone
              having to keep it up to date — and it is never stored, because a
              total that could disagree with the rows above it is worse than
              no total at all. */}
          <div className="border-b border-gray-300 flex bg-gray-50">
            {/* Narrow padding and no wrapping: "12 hr 45 min" has to stay on
                one line in a 90px column, or the row reads as two totals. */}
            <div className="px-1 py-3 border-r border-gray-300 flex flex-col items-center justify-center text-center" style={{width: '90px', minWidth: '90px'}}>
              <span className="text-[10px] font-bold uppercase tracking-wide text-gray-600">Total</span>
              <span className="whitespace-nowrap text-xs font-bold text-gray-800" data-testid="planner-total-duration">
                {totalActivityTime}
              </span>
            </div>
            <div className="p-3 border-r border-gray-300 flex items-center text-xs text-gray-500" style={{width: 'calc(100% - 410px)'}}>
              Total planned activity time
            </div>
            <div className="p-3" style={{width: '320px', minWidth: '320px'}} />
          </div>

          {/* Post-Assessment and Summary */}
          <div className="border-b border-gray-300 flex">
            <div className="bg-gray-100 p-3 font-bold border-r border-gray-300" style={{width: '160px', minWidth: '160px'}}>
              <div className="flex items-start h-full pt-1">Post-Assessment</div>
            </div>
            <div className="p-3 border-r border-gray-300" style={{width: 'calc(100% - 460px)'}}>
              <PlannerTextField
                {...lessonField('postAssessment', 'Post-Assessment')}
                label={null}
                minHeight={180}
                placeholder="How will you assess learning outcomes?"
              />
            </div>
            <div className="p-3" style={{width: '300px', minWidth: '300px'}}>
              <PlannerTextField
                {...lessonField('summary', 'Summary')}
                minHeight={180}
                placeholder="Summarize key learning points"
              />
            </div>
          </div>

          {/* Reflections */}
          <div className="flex">
            <div className="bg-gray-100 p-3 font-bold border-r border-gray-300" style={{width: '160px', minWidth: '160px'}}>
              <div className="flex items-start h-full pt-1">Reflections on the Lesson</div>
            </div>
            <div className="flex-1 p-3">
              <PlannerTextField
                {...lessonField('reflections', 'Reflections on the Lesson')}
                label={null}
                minHeight={150}
                placeholder="Reflect on what worked well, what could be improved, and lessons learned for next time"
              />
            </div>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 text-sm text-gray-600 no-print">
          <p><strong>Instructions:</strong></p>
          <ul className="list-disc ml-5 mt-2 space-y-1">
            <li>Fill out each section of the lesson plan template</li>
            <li>Drag the bottom-right corner of any box to make it bigger — the size is remembered</li>
            <li>Click <strong>Editor</strong> on any box for bold, italic, underline, headings, colours, font sizes and bullet or numbered lists</li>
            <li>Attach reference material to a learner activity — a file (.txt, .md, .json, .pdf, .docx) or a web link. It travels with the plan into the AI Prompt page and the HTML export</li>
            <li>Use the "+" button on an activity to add another one directly below it</li>
            <li>Drag an activity by its handle to move it, or focus the handle and use the up and down arrow keys</li>
            <li>Click "Load Sample" to see an example lesson plan</li>
            <li>Save your work as a JSON file using the "Save JSON" button</li>
            <li>Load a previously saved lesson plan using the "Upload JSON" button — plans saved by earlier versions load unchanged</li>
            <li>Paste JSON directly using the "Paste JSON" button</li>
            <li>Export a shareable HTML version using the "Export HTML" button</li>
            <li>Print a professional copy using the "Print" button (optimized for landscape)</li>
            <li>Reset the form to a blank state using the "Reset" button</li>
            <li><strong>Nothing here is saved in your browser.</strong> Closing or reloading the tab clears the form, and nothing is left behind on a shared or borrowed computer. Use "Save JSON" as you work, and "Upload JSON" to carry on later</li>
            <li>The template follows the BOPPPS model: Bridge-in, Objectives, Pre-assessment, Presentation/Practice/Participation, Post-assessment, Summary</li>
          </ul>
        </div>
      </div>

      {/* Formatting editor */}
      {editorTarget && (
        <Suspense fallback={(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="flex items-center gap-2 rounded-lg bg-white px-5 py-4 text-sm text-gray-700 shadow-xl">
              <Loader2 size={16} className="animate-spin" />
              Opening the editor…
            </div>
          </div>
        )}
        >
          <PlannerRichTextModal
            title={editorTarget.label}
            html={getRichText(editorValue, editorTarget.field)}
            plainText={editorValue ? editorValue[editorTarget.field] : ''}
            onSave={handleEditorSave}
            onCancel={() => setEditorTarget(null)}
          />
        </Suspense>
      )}

      {/* Paste JSON Modal */}
      {showPasteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ClipboardPaste size={20} className="text-teal-500" />
                Paste JSON
              </h2>
              <button
                onClick={() => setShowPasteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-6 py-4 flex flex-col gap-3">
              <p className="text-sm text-gray-600">
                Paste a BOPPPS lesson plan JSON below and click <strong>Apply</strong> to load it into the form.
              </p>
              <textarea
                className="w-full h-64 p-3 border border-gray-300 rounded-lg font-mono text-sm resize-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder='{"lessonTopic": "...", "bridgeIn": "...", ...}'
                value={pasteJsonText}
                onChange={(e) => { setPasteJsonText(e.target.value); setPasteJsonError(''); }}
                spellCheck={false}
              />
              {pasteJsonError && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                  <AlertCircle size={16} className="flex-shrink-0" />
                  {pasteJsonError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowPasteModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyPastedJson}
                disabled={!pasteJsonText.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BopppsPlanner;
